// PWA 第四階段「離線登入」——線上登入成功後把密碼衍生成一組不可逆推的本機
// 驗證值存進獨立的 IndexedDB（`pcces-offline-login`，跟 `Common/offlineDb.js`
// 那份共用的 `pcces-offline`「唯讀共用主檔」資料庫刻意分開，這裡存的是認證
// 相關資料，隔離開來比較容易單獨稽核／清除），離線時可以用同一組帳密在本機
// 驗證身分、拿到側邊選單等頁殼（已由 SW 快取）繼續看本機資料，不需要連線。
//
// ⚠⚠⚠ 資安威脅模型（完整版見 docs/modules/security-audit.md「PWA 離線登入」
// 那段，這裡摘要）：
//   - **完全不存明碼密碼、不存伺服器 Session、不存 anti-forgery token**——
//     存的是 PBKDF2（SHA-256、210,000 次、隨機 16 bytes salt）衍生出來的
//     32 bytes 驗證值，單向、不可逆推回密碼。
//   - **接受的風險**：裝置遺失＋攻擊者知道密碼＝可以在該裝置離線登入、看到
//     那個帳號快取在本機的資料（IndexedDB 唯讀主檔快照、SW 快取的頁殼）。
//     這不是新風險——攻擊者本來就需要知道密碼；真正的風險是「裝置本身被拿走
//     且未上鎖」，這是作業系統層級的裝置安全，不是這裡能解的問題。
//   - **不設時效（2026-09-30 使用者裁示）**：離線登入資料只在下一次線上帳密
//     登入成功時被新值覆寫（等於改密碼後自動更新），不會單獨過期——使用者
//     原話「不用設期限，反正使用者可以使用線上編輯，放棄離線認領的內容」。
//     這跟認領（租約）本身的到期時間是兩件事，租約到期規則不受影響。
//   - **不能拿來打 API**：離線登入只在本機比對成功、寫一個 sessionStorage
//     標記，完全沒有建立任何伺服器端 Session 或可重放的憑證，回線後所有
//     API 呼叫一樣需要重新走 `/Login/SignIn` 才能拿到真正的 Session。
//   - **本機暴力猜測**：210,000 次 PBKDF2 疊代讓離線暴力猜測的成本大幅提高
//     （不是不可能，是不划算），跟一般「密碼強度是第一道防線」的既有假設
//     一致，這裡沒有、也不可能引入新的密碼強度保證。
'use strict'

const DB_NAME = 'pcces-offline-login'
const DB_VERSION = 1
const STORE_NAME = 'credentials'
const PBKDF2_ITERATIONS = 210_000
const MAX_ATTEMPTS = 5
const LOCKOUT_MINUTES = 15
const LOCKOUT_STORAGE_KEY = 'pcces-offline-login-lockout'
export const OFFLINE_SESSION_KEY = 'pcces-offline-login-session'

// ── IndexedDB 開啟（獨立資料庫，見檔頭說明；不重用 Common/offlineDb.js 的
//   'pcces-offline'，那份是給唯讀共用主檔用的，語意跟這裡的認證資料不同）──
let dbPromise = null
function openDb() {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('IndexedDB not available')); return }
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE_NAME)) {
        req.result.createObjectStore(STORE_NAME) // key: 正規化過的 userNo
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
  return dbPromise
}

function normalizeUserNo(userNo) {
  return String(userNo || '').trim().toLowerCase()
}

async function dbGet(key) {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const req = tx.objectStore(STORE_NAME).get(key)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function dbSet(key, value) {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    tx.objectStore(STORE_NAME).put(value, key)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

function toBase64(bytes) {
  let binary = ''
  bytes.forEach((b) => { binary += String.fromCharCode(b) })
  return btoa(binary)
}

function fromBase64(b64) {
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

/** PBKDF2-SHA256 衍生，回傳 32 bytes（Uint8Array）。`saltBytes` 由呼叫端決定
 * （新建憑證時隨機產生；驗證時用既有記錄裡存的那組，兩邊才會算出同一個值）。 */
async function deriveBits(password, saltBytes, iterations) {
  const enc = new TextEncoder()
  const keyMaterial = await crypto.subtle.importKey(
    'raw', enc.encode(password), 'PBKDF2', false, ['deriveBits'],
  )
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: saltBytes, iterations, hash: 'SHA-256' },
    keyMaterial, 256,
  )
  return new Uint8Array(bits)
}

/** 常數時間比較（避免用 `===`／逐位元比對時因為短路提前中止而洩漏「前幾個
 * byte 對不對」的時序資訊——單一裝置本機比對這個風險本來就很低，但成本
 * 幾乎是零，順手做對。） */
function constantTimeEqual(a, b) {
  if (a.length !== b.length) return false
  let diff = 0
  for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i]
  return diff === 0
}

// ⚠⚠ deviceId：95 之後會新增 `Common/deviceId.js`（跨頁共用的裝置識別碼產生器）
// ——這裡先用最簡單的 localStorage＋`crypto.randomUUID()` 內嵌一份等效邏輯，
// 不要另外建一個同名檔案佔位（避免之後兩份 `Common/deviceId.js` 互相覆蓋的
// git 衝突）。**之後 `Common/deviceId.js` 落地後，把這支函式改成 import 那支
// 即可**，這裡存的欄位名稱（`deviceId`）已經跟他們約定好，不用跟著改資料結構。
function getDeviceId() {
  try {
    const KEY = 'pcces-device-id'
    let id = localStorage.getItem(KEY)
    if (!id) {
      id = (crypto.randomUUID ? crypto.randomUUID() : toBase64(crypto.getRandomValues(new Uint8Array(16))))
      localStorage.setItem(KEY, id)
    }
    return id
  } catch {
    return 'unknown-device' // localStorage 不可用（無痕模式極端情況）——不擋主流程
  }
}

/** 線上登入成功後呼叫——把這次的帳密衍生存進本機，供之後離線登入使用。
 * `password` 只在這個函式的執行期間存在於記憶體，衍生完成後不保留任何參照。 */
export async function saveOfflineCredential({ userNo, password, userSeq, displayName, roleSeq }) {
  try {
    const salt = crypto.getRandomValues(new Uint8Array(16))
    const derived = await deriveBits(password, salt, PBKDF2_ITERATIONS)
    const now = Date.now()
    await dbSet(normalizeUserNo(userNo), {
      userNo: String(userNo || '').trim(),
      userSeq,
      displayName,
      roleSeq,
      deviceId: getDeviceId(),
      salt: toBase64(salt),
      hash: toBase64(derived),
      iterations: PBKDF2_ITERATIONS,
      createdAt: now,
    })
  } catch (e) {
    // 存不進去（IndexedDB 不可用／無痕模式）不影響這次線上登入本身——離線登入
    // 只是錦上添花，不能讓它的失敗擋住正常登入流程。
    console.error('[offlineAuth] saveOfflineCredential 失敗', e)
  }
}

/** 這個帳號在本機是否有離線登入憑證——Login.vue 用這個決定要不要顯示
 * 「離線登入」表單（沒有憑證就不用顯示，顯示了使用者也一定會失敗）。
 * 不傳 `userNo` 時查「最近一筆」，用來判斷「這台裝置上到底有沒有任何人存過」。
 * ⚠ 不設時效（見檔頭說明），只要存在就算有效。 */
export async function hasOfflineCredentialForUser(userSeq) {
  if (!userSeq) return false
  try {
    const db = await openDb()
    const records = await new Promise((resolve, reject) => {
      const request = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAll()
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
    return records.some((r) => r.userSeq === userSeq)
  } catch { return false }
}

export async function hasUsableOfflineCredential(userNo) {
  if (!userNo) return false
  try {
    const record = await dbGet(normalizeUserNo(userNo))
    return !!record
  } catch {
    return false
  }
}

/** 鎖定狀態：{ lockedUntil: number|null, attempts: number } */
function readLockoutState() {
  try {
    const raw = localStorage.getItem(LOCKOUT_STORAGE_KEY)
    return raw ? JSON.parse(raw) : { attempts: 0, lockedUntil: null }
  } catch {
    return { attempts: 0, lockedUntil: null }
  }
}

function writeLockoutState(state) {
  try { localStorage.setItem(LOCKOUT_STORAGE_KEY, JSON.stringify(state)) } catch { /* 存不進去就算了，見下方說明 */ }
}

/** 目前是不是被鎖定中，回傳鎖定剩餘毫秒數（0＝沒有鎖定）。
 * ⚠ 這是**本機**鎖定（localStorage，容易被清除重來）——攻擊模型本來就是
 * 「裝置在攻擊者手上」，這道鎖擋的是隨手亂猜，不是有意繞過儲存機制的攻擊者；
 * 真正的防線是 210,000 次 PBKDF2 疊代本身的計算成本，鎖定只是使用者體驗上的
 * 額外摩擦，不是安全性保證——這點在 security-audit.md 有講清楚，不要誤以為
 * 這道鎖是主要防線。 */
export function getLockoutRemainingMs() {
  const state = readLockoutState()
  if (!state.lockedUntil) return 0
  const remaining = state.lockedUntil - Date.now()
  return remaining > 0 ? remaining : 0
}

function recordFailedAttempt() {
  const state = readLockoutState()
  const attempts = (state.lockedUntil && state.lockedUntil > Date.now() ? 0 : state.attempts) + 1
  const next = { attempts, lockedUntil: state.lockedUntil }
  if (attempts >= MAX_ATTEMPTS) {
    next.lockedUntil = Date.now() + LOCKOUT_MINUTES * 60 * 1000
    next.attempts = 0
  }
  writeLockoutState(next)
}

function resetLockout() {
  writeLockoutState({ attempts: 0, lockedUntil: null })
}

/** 離線登入驗證。回傳：
 *   { ok:true, user:{userSeq,displayName,roleSeq,userNo} }
 *   { ok:false, reason:'locked', remainingMs }
 *   { ok:false, reason:'not-found' | 'mismatch' }
 * ⚠ 不再有 'expired'——離線登入資料不設時效（見檔頭說明），只在下一次線上
 * 登入成功時被新值覆寫。
 */
export async function verifyOfflineCredential(userNo, password) {
  const remainingMs = getLockoutRemainingMs()
  if (remainingMs > 0) return { ok: false, reason: 'locked', remainingMs }

  const record = await dbGet(normalizeUserNo(userNo)).catch(() => null)
  if (!record) return { ok: false, reason: 'not-found' }

  const derived = await deriveBits(password, fromBase64(record.salt), record.iterations || PBKDF2_ITERATIONS)
  const matches = constantTimeEqual(derived, fromBase64(record.hash))
  if (!matches) {
    recordFailedAttempt()
    return { ok: false, reason: 'mismatch' }
  }

  resetLockout()
  return {
    ok: true,
    user: {
      userSeq: record.userSeq,
      displayName: record.displayName,
      roleSeq: record.roleSeq,
      userNo: record.userNo,
    },
  }
}

/** 離線登入成功後寫入的 sessionStorage 標記——只在瀏覽器分頁存活期間有效
 * （關掉分頁就消失，不是持久登入狀態），`Common/api.js`／
 * `Common/data/currentUser.js` 讀這個判斷「目前是不是離線登入狀態」。 */
export function setOfflineLoginMarker(user) {
  try {
    sessionStorage.setItem(OFFLINE_SESSION_KEY, JSON.stringify({ ...user, loggedInAt: Date.now() }))
  } catch { /* sessionStorage 不可用就沒有這層狀態，回線後的行為退回既有的 -1919 導頁 */ }
}

export function getOfflineLoginMarker() {
  try {
    const raw = sessionStorage.getItem(OFFLINE_SESSION_KEY)
    const user = raw ? JSON.parse(raw) : null
    if (!user?.userSeq) return null
    return user
  } catch {
    return null
  }
}

export function clearOfflineLoginMarker() {
  try { sessionStorage.removeItem(OFFLINE_SESSION_KEY) } catch { /* 沒有就算了 */ }
}
